/**
 * Tenant primary-colour → runtime brand tokens (the white-label bridge).
 *
 * tenant-service's public branding projection carries one hex `primaryColor`,
 * while `applyBrandTokens` expects full OKLCH scale ladders (see brand.css).
 * The bridge between them deliberately does NOT try to invent a palette from
 * the hex: it keeps brand.css's exact lightness ladder — which is what the
 * app's contrast contract is tuned against (white on primary-500 ≥ 4.5:1,
 * primary-as-text on background-50 ≥ 4.5:1, see brand.css header) — and swaps
 * in the tenant's HUE, scaling chroma proportionally to how saturated their
 * colour is relative to the default brand. Contrast is a function of
 * lightness, so a re-hued ladder holds the contract for any tenant colour;
 * a naive "generate shades from the hex" does not.
 *
 * Only the `primary` scale is overridden. Background/foreground/accent stay
 * the product's own — a tenant re-skins the brand colour and logo, not the
 * entire design system (the same boundary the shell's applyBrandTokens draws).
 */

import {
  applyBrandTokens,
  clearBrandTokens,
  type InkStep,
  type ScaleStep,
  type OklchTriple,
} from './index';

/** brand.css's default primary ladder — [step, L, C]; hue is replaced. */
const LIGHT_LADDER: [ScaleStep, number, number][] = [
  [50, 0.95, 0.03],
  [100, 0.88, 0.06],
  [200, 0.8, 0.09],
  [300, 0.7, 0.12],
  [400, 0.56, 0.14],
  [500, 0.42, 0.16],
  [600, 0.36, 0.15],
  [700, 0.3, 0.14],
  [800, 0.24, 0.12],
  [900, 0.18, 0.09],
  [950, 0.12, 0.06],
];

const DARK_LADDER: [ScaleStep, number, number][] = [
  [50, 0.18, 0.06],
  [100, 0.22, 0.09],
  [200, 0.28, 0.11],
  [300, 0.34, 0.13],
  [400, 0.38, 0.15],
  [500, 0.45, 0.14],
  [600, 0.62, 0.12],
  [700, 0.72, 0.1],
  [800, 0.8, 0.08],
  [900, 0.87, 0.06],
  [950, 0.93, 0.03],
];

/** brand.css's secondary ("warm support") ladders — re-hued from the tenant's secondaryColor. */
const SECONDARY_LIGHT_LADDER: [ScaleStep, number, number][] = [
  [50, 0.95, 0.04],
  [100, 0.9, 0.08],
  [200, 0.82, 0.12],
  [300, 0.74, 0.15],
  [400, 0.66, 0.17],
  [500, 0.6, 0.18],
  [600, 0.5, 0.16],
  [700, 0.42, 0.14],
  [800, 0.34, 0.12],
  [900, 0.26, 0.09],
  [950, 0.18, 0.06],
];

const SECONDARY_DARK_LADDER: [ScaleStep, number, number][] = [
  [50, 0.22, 0.06],
  [100, 0.28, 0.08],
  [200, 0.36, 0.1],
  [300, 0.44, 0.12],
  [400, 0.52, 0.14],
  [500, 0.58, 0.15],
  [600, 0.66, 0.12],
  [700, 0.74, 0.1],
  [800, 0.82, 0.08],
  [900, 0.88, 0.06],
  [950, 0.94, 0.03],
];

/** The default secondary's chroma at 500 — its own scaling reference. */
const DEFAULT_SECONDARY_CHROMA_500 = 0.18;

/**
 * The `ink` scale — the brand colour used as a SURFACE (hero balance card,
 * bottom tab bar; see brand.css "Brand ink"). It is as much the brand as
 * `primary` is, so a tenant re-skin must carry it too or the largest surfaces
 * on screen stay the default maroon. Same values as light primary-700/800/900.
 */
const INK_LADDER: [InkStep, number, number][] = [
  [700, 0.3, 0.14],
  [800, 0.24, 0.12],
  [900, 0.18, 0.09],
];

/** The default brand's chroma at the 500 step — the scaling reference. */
const DEFAULT_CHROMA_500 = 0.16;

/** OKLCH gamut safety: past this, low-lightness steps clip in sRGB. */
const CHROMA_CAP = 0.27;

/** The shipped default brand — matching it exactly means "no override needed". */
const DEFAULT_PRIMARY_HEX = '#8b1a3c';

interface Oklch {
  l: number;
  c: number;
  h: number;
}

/** sRGB hex → OKLCH (Björn Ottosson's OKLab, the same space brand.css uses). */
export function hexToOklch(hex: string): Oklch | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;

  const int = parseInt(match[1], 16);
  const linear = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const r = linear((int >> 16) & 0xff);
  const g = linear((int >> 8) & 0xff);
  const b = linear(int & 0xff);

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const c = Math.sqrt(a * a + bb * bb);
  let h = (Math.atan2(bb, a) * 180) / Math.PI;
  if (h < 0) h += 360;

  return { l: L, c, h };
}

function ladderFor(
  ladder: [ScaleStep, number, number][],
  hue: number,
  chromaScale: number,
): Partial<Record<ScaleStep, OklchTriple>> {
  const out: Partial<Record<ScaleStep, OklchTriple>> = {};
  for (const [step, l, c] of ladder) {
    const chroma = Math.min(c * chromaScale, CHROMA_CAP);
    out[step] = `${l} ${chroma.toFixed(3)} ${hue.toFixed(1)}`;
  }
  return out;
}

/**
 * Applies the tenant's palette as runtime scale overrides, or clears every
 * override when the tenant is on the default brand (so the default deployment
 * stays pixel-identical to brand.css).
 *
 * Mapping — every brand-carrying scale derives from the tenant's two colours:
 *   primaryColor   → `primary` (buttons, links, active states) + `ink`
 *                    (brand-as-surface: hero card, bottom bar)
 *   secondaryColor → `secondary` (the warm support tint: feature chips, badges)
 *
 * `accent` (the gold) is deliberately NOT re-hued: in a gold-savings product it
 * is the colour OF THE METAL — gold balances, accrual figures, redemption marks
 * — a product semantic like success-green, not a brand hue.
 */
export function applyTenantBranding(
  primaryColor: string | undefined | null,
  secondaryColor?: string | null,
): string | null {
  const hex = (primaryColor ?? '').trim().toLowerCase();
  if (!hex || hex === DEFAULT_PRIMARY_HEX) {
    clearBrandTokens();
    return null;
  }

  const oklch = hexToOklch(hex);
  if (!oklch) return null; // an unparseable colour must not un-style the app

  // How saturated the tenant's brand is, relative to ours. A muted brand
  // (bank-grey) keeps its mutedness across the ladder; capped so a neon brand
  // cannot push low steps out of the sRGB gamut.
  const chromaScale = Math.min(oklch.c / DEFAULT_CHROMA_500, 1.6);

  const ink: Partial<Record<InkStep, OklchTriple>> = {};
  for (const [step, l, c] of INK_LADDER) {
    ink[step] = `${l} ${Math.min(c * chromaScale, CHROMA_CAP).toFixed(3)} ${oklch.h.toFixed(1)}`;
  }

  const light: NonNullable<Parameters<typeof applyBrandTokens>[0]['light']> = {
    primary: ladderFor(LIGHT_LADDER, oklch.h, chromaScale),
  };
  const dark: NonNullable<Parameters<typeof applyBrandTokens>[0]['dark']> = {
    primary: ladderFor(DARK_LADDER, oklch.h, chromaScale),
  };

  const secondary = hexToOklch((secondaryColor ?? '').trim().toLowerCase());
  if (secondary) {
    const secondaryScale = Math.min(secondary.c / DEFAULT_SECONDARY_CHROMA_500, 1.6);
    light.secondary = ladderFor(SECONDARY_LIGHT_LADDER, secondary.h, secondaryScale);
    dark.secondary = ladderFor(SECONDARY_DARK_LADDER, secondary.h, secondaryScale);
  }

  // Returns the generated stylesheet so `initTenant` can cache it against this host
  // and the boot script can replay it before first paint on the next visit.
  return applyBrandTokens({ light, dark, ink });
}
